import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import PageHero from './PageHero';
import { getProducts, getCategories, mapProductToFrontend } from '../services/productsService';
import { getAllCMSImages } from '../services/s3Service';
import { getFirstCachedImage } from '../utils/cmsCache';
import './../App.css';
import Modal from './Modal';
import useModal from '../hooks/useModal';

const styles = {
  loadingContainer: {
    textAlign: 'center',
    padding: '48px 24px',
    minHeight: 200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#666'
  },
  spinner: {
    display: 'inline-block',
    width: 32,
    height: 32,
    border: '3px solid #e0e0e0',
    borderTopColor: '#0039A6',
    borderRadius: '50%'
  },
  loadingLabel: {
    marginTop: 12,
    marginBottom: 0,
    color: '#666',
    fontSize: 15
  }
};

const ShopPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, modalProps } = useModal();
  const [selectedCategory, setSelectedCategory] = useState(null); 
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [heroImage, setHeroImage] = useState(() => getFirstCachedImage(['page_hero_shop']));
  const location = useLocation();
  const productRefs = useRef({});

  useEffect(() => {
    getAllCMSImages().then((imgs) => setHeroImage(imgs.page_hero_shop || null))
      .catch(() => setHeroImage(null));
  }, []);
  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [selectedCategory, searchTerm, currentPage]);

  const loadCategories = async () => {
    try {
      const cats = await getCategories();
      setCategories(cats);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 20
      };

      if (selectedCategory) {
        params.categoria_id = selectedCategory;
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const { productos, pagination: pag } = await getProducts(params);
      
      const mappedProducts = productos.map(mapProductToFrontend);
      setProducts(mappedProducts);
      setPagination(pag);
    } catch (error) {
      console.error('Error al cargar productos:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); 
    loadProducts();
  };

  const getImageSrc = (img) => {
    if (!img || img === '' || img === null) {
      return `${process.env.PUBLIC_URL}/contenido-no-disponible.jpeg`;
    }
    
    if (img.startsWith('http')) return img;
    
    return process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}${img}` : img;
  };

  useEffect(() => {
    if (location.hash) {
      const productId = location.hash.replace('#product-', '');
      setTimeout(() => {
        const element = productRefs.current[productId];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.style.outline = '3px solid #D52B1E';
          element.style.outlineOffset = '5px';
          setTimeout(() => {
            element.style.outline = '';
            element.style.outlineOffset = '';
          }, 2000);
        }
      }, 300);
    }
  }, [location.hash, products]);

  const handleAddToCart = (e, product) => {
    // Evitar que el click haga bubble al card y navegue al detalle
    e.stopPropagation();

    try {
      // Mismo formato y key que ProductDetailPage y CartPage: localStorage 'cart'
      const localCart = JSON.parse(localStorage.getItem('cart') || '[]');
      const existingItem = localCart.find(item => item.producto_id === product.id);

      if (existingItem) {
        existingItem.cantidad += 1;
      } else {
        localCart.push({
          producto_id: product.id,
          cantidad: 1,
          producto: {
            nombre: product.name,
            precio: product.discountPrice || product.price,
            imagen_url: product.image || null
          }
        });
      }

      localStorage.setItem('cart', JSON.stringify(localCart));
      // Disparar evento para que Header y CartPage se actualicen
      window.dispatchEvent(new Event('cart-updated'));

      showSuccess('Carrito actualizado', `¡${product.name} agregado al carrito!`);
    } catch (error) {
      console.error('Error al añadir al carrito:', error);
      showError('Error', error.message || 'No se pudo añadir al carrito');
    }
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  return (
    <div className="main-container">
      <Header />
      <PageHero
        eyebrow="Tienda oficial"
        imageUrl={heroImage}
        title="Tienda Oficial ACHIPO"
        subtitle="Equipamiento y ropa oficial para powerlifters"
      />
      <div className="shop-container">
        {/* Filtros y Búsqueda */}
        <div className="shop-filters" style={{ marginTop: '-3cm' }}>
          <div className="filter-section">
            <h3>Filtrar por Categoría</h3>
            <div className="category-filters">
              <button 
                type="button"
                className={`btn btn-filter ${selectedCategory === null ? 'active' : ''}`}
                onClick={() => handleCategoryChange(null)}
              >
                Todos
              </button>
              {categories.map(category => (
                <button 
                  key={category.id}
                  type="button"
                  className={`btn btn-filter ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category.id)}
                >
                  {category.nombre}
                </button>
              ))}
            </div>
          </div>
          
          <form onSubmit={handleSearch} className="search-section">
            <input 
              type="text" 
              placeholder="Buscar productos..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit" className="btn btn-primary search-btn-lupa">🔍</button>
          </form>
        </div>

        {/* Loading */}
        {loading && (
          <div className="shop-loading-container" style={styles.loadingContainer}>
            <span className="shop-loading-spinner" style={styles.spinner} aria-hidden="true" />
            <p style={styles.loadingLabel}>Cargando productos...</p>
            <style>{`
              @keyframes shopSpinner {
                to { transform: rotate(360deg); }
              }
              .shop-loading-spinner {
                animation: shopSpinner 0.8s linear infinite;
              }
            `}</style>
          </div>
        )}

        {/* Grid de Productos */}
        {!loading && products.length > 0 && (
          <>
            <div className="products-grid">
              {products.map(product => (
                <div 
                  key={product.id} 
                  id={`product-${product.id}`}
                  ref={el => productRefs.current[product.id] = el}
                  className="product-card product-card-full-image"
                  onClick={() => navigate(`/product/${product.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="product-card-image-wrap">
                    {product.featured && (
                      <span className="product-card-badge-star" title="Destacado">⭐</span>
                    )}
                    <span className="product-category product-card-category">{product.category}</span>
                    <img 
                      src={getImageSrc(product.image)} 
                      alt={product.name}
                      className="product-card-img"
                      onError={(e) => {
                        const defaultImage = `${process.env.PUBLIC_URL}/contenido-no-disponible.jpeg`;
                        if (e.target.src !== defaultImage && !e.target.src.includes('contenido-no-disponible.jpeg')) {
                          e.target.src = defaultImage;
                        }
                      }}
                    />
                  </div>
                  <div className="product-card-overlay" onClick={(e) => e.stopPropagation()}>
                    <h3 className="product-name">{product.name}</h3>
                    {product.stock > 0 ? (
                      <p className="product-card-stock">Stock: {product.stock}</p>
                    ) : (
                      <p className="product-card-stock product-card-stock-none">Sin stock</p>
                    )}
                    <div className="product-footer">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {product.discountPrice && (
                          <span className="product-price-old">${product.price.toLocaleString('es-CL')}</span>
                        )}
                        <span className="product-price">${(product.discountPrice || product.price).toLocaleString('es-CL')}</span>
                      </div>
                      <button 
                        type="button"
                        className="btn btn-primary add-to-cart-btn"
                        onClick={(e) => handleAddToCart(e, product)}
                        disabled={product.stock === 0}
                        title={product.stock === 0 ? "Sin stock" : "Agregar al carrito"}
                        style={{
                          opacity: product.stock === 0 ? 0.5 : 1,
                          cursor: product.stock === 0 ? 'not-allowed' : 'pointer',
                          padding: '8px 12px'
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" style={{ width: '20px', height: '20px' }}>
                          <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginación */}
            {pagination && pagination.total_pages > 1 && (
              <div className="pagination" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '10px', 
                marginTop: '40px',
                marginBottom: '40px'
              }}>
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="btn btn-primary"
                >
                  ← Anterior
                </button>
                <span style={{ 
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  borderRadius: '4px'
                }}>
                  Página {currentPage} de {pagination.total_pages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.min(pagination.total_pages, prev + 1))}
                  disabled={currentPage === pagination.total_pages}
                  className="btn btn-primary"
                >
                  Siguiente →
                </button>
              </div>
            )}
          </>
        )}

        {/* Mensaje si no hay productos */}
        {!loading && products.length === 0 && (
          <div className="no-products">
            <p>No hay productos disponibles</p>
            {(selectedCategory || searchTerm) && (
              <button 
                type="button"
                onClick={() => {
                  setSelectedCategory(null);
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                className="btn btn-primary"
              >
                Ver Todos los Productos
              </button>
            )}
          </div>
        )}
      </div>
      
      <Footer />
      <Modal {...modalProps} />
    </div>
  );
};

export default ShopPage;